import React, { useEffect, useState } from "react";
import "leaflet/dist/leaflet.css";
import OptimizeRouteResult from "./OptimizeRouteResult";
import AddressAutocomplete from "./AddressAutocomplete";

const apiBaseUrl = process.env.REACT_APP_API_URL;

const toLocalInputValue = (iso, tz) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const opts = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: tz || undefined,
    };
    const parts = new Intl.DateTimeFormat("en-CA", opts).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
  } catch {
    return "";
  }
};

const formatTime = (iso, tz) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz || undefined,
    });
  } catch {
    return iso;
  }
};

const OptimizeRouteModal = ({ isOpen, onClose, uniqueCode, onApplied }) => {
  const [stage, setStage] = useState("loading"); // loading | form | submitting | result
  const [context, setContext] = useState(null);
  const [startAddress, setStartAddress] = useState("");
  const [arriveBy, setArriveBy] = useState("");
  const [bufferMinutes, setBufferMinutes] = useState(2);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setStage("loading");
    setError(null);
    setResult(null);
    fetch(`${apiBaseUrl}/api/event-participations/${uniqueCode}/route-context`)
      .then((r) => r.json().then((b) => ({ ok: r.ok, b })))
      .then(({ ok, b }) => {
        if (cancelled) return;
        if (!ok) {
          setError(b?.message || "Failed to load route context.");
          setStage("form");
          return;
        }
        setContext(b);
        setStartAddress(b.savedStart?.address || "");
        setArriveBy(toLocalInputValue(b.eventTime, b.eventTimeZone));
        setStage("form");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Network error.");
        setStage("form");
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, uniqueCode]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!arriveBy) {
      setError("Please choose an arrival time.");
      return;
    }
    setStage("submitting");
    setError(null);
    try {
      const resp = await fetch(
        `${apiBaseUrl}/api/event-participations/${uniqueCode}/optimize-route`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startAddress: startAddress || undefined,
            arriveBy: arriveBy.replace("T", " ") + ":00",
            arriveByTimezone: context?.eventTimeZone || undefined,
            bufferMinutes: Number(bufferMinutes) || 0,
          }),
        },
      );
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(body?.message || "Could not optimize route.");
      }
      setResult(body);
      setStage("result");
    } catch (err) {
      setError(err.message || "Could not optimize route.");
      setStage("form");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl flex flex-col"
        style={{ maxHeight: "92vh" }}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold">Optimize Pickup Route</h3>
            {context && (
              <p className="text-xs text-gray-500">
                {context.pickupsWithCoords} pickup
                {context.pickupsWithCoords === 1 ? "" : "s"} with GPS
                {context.pickupsMissingCoords > 0
                  ? ` · ${context.pickupsMissingCoords} missing GPS (not included)`
                  : ""}
              </p>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {stage === "loading" && (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          )}

          {(stage === "form" || stage === "submitting") && (
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              {error && (
                <div className="alert alert-error text-sm">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Starting address
                </label>
                <AddressAutocomplete
                  value={startAddress}
                  onChange={setStartAddress}
                  uniqueCode={uniqueCode}
                  disabled={stage === "submitting"}
                  placeholder="123 Main St, Apt 4"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Arrive by
                  </label>
                  <input
                    type="datetime-local"
                    className="input input-bordered w-full"
                    value={arriveBy}
                    onChange={(e) => setArriveBy(e.target.value)}
                    disabled={stage === "submitting"}
                    required
                  />
                  {context?.eventTimeZone && (
                    <p className="text-xs text-gray-500 mt-1">
                      Event timezone: {context.eventTimeZone}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Buffer per pickup (min)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="60"
                    className="input input-bordered w-full"
                    value={bufferMinutes}
                    onChange={(e) => setBufferMinutes(e.target.value)}
                    disabled={stage === "submitting"}
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  className="btn bg-[#004a7e] text-white hover:bg-[#003a64]"
                  disabled={stage === "submitting"}
                >
                  {stage === "submitting" ? "Optimizing…" : "Optimize Route"}
                </button>
              </div>
            </form>
          )}

          {stage === "result" && result && (
            <OptimizeRouteResult
              result={result}
              timezone={context?.eventTimeZone}
              formatTime={formatTime}
              uniqueCode={uniqueCode}
              onEdit={() => setStage("form")}
              onApplied={() => {
                onApplied?.();
                onClose();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OptimizeRouteModal;
